import { OnDestroy, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MessageCatalogModalComponent } from '../../message-catalog-modal/message-catalog-modal.component';
import { HTTPWrapperContext, RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES} from '@ptl/xhr';
import { environment } from 'src/environments/environment';
import { MessageCatalogPayloadStructure, MessageCatalogResponse, MessageArrayStructure, MessagesResponseStructure } from '../interfaces/message-catalog-modal.interface';
import { BrandingConfigurationService } from '@ptl/brand';
import { ReducerUtilityService, StateStructure } from '@ptl/state';
import { PlanUtilityService } from 'src/app/angular-elements/plan-selection/plan-summary-list/plan-utility.service';
import { PlansStateStructure } from 'src/app/angular-elements/plan-selection/configs/interfaces/plansStateStructure.interface';
import { Observable, Subscription, Subject } from 'rxjs';
import { select, NgRedux } from '@ptl/angular-redux-store';
import { clearPlanDetails } from 'src/app/angular-elements/plan-selection/configs/reducers/plan-details.reducer';
import { take, filter } from 'rxjs/operators';
import { Product } from 'src/app/angular-elements/plan-selection/interfaces/product.interface';
import { PlanAttributes, SummaryView } from 'src/app/angular-elements/plan-selection/interfaces/plan-attributes.interface';

@Injectable({
    providedIn: 'root'
})

export class MessageCatalogModalTriggerService implements OnDestroy {

    @select(['componentStates', 'Plans'])
    plansStateChanges$: Observable<any>;
    plansStateSubscription: Subscription;
  
    @select(['componentStates', 'PlanDetails', 'planDetails'])
    planDetailsStateChanges$: Observable<any>;
    planDetailsStateSubscription: Subscription;

    displayResponseSubject: Subject<[Map<string,string>, boolean]> = new Subject();

    constructor(
        private matDialog: MatDialog,
        private httpClient: HTTPWrapperContext,
        private brandService: BrandingConfigurationService,
        private reducerUtilityService: ReducerUtilityService,
        private planUtilityService: PlanUtilityService,
        private ngRedux: NgRedux<StateStructure>
      ) { }
    
    checkPlanAndZipMessages(messageCatalogContent: Map<string, any>, isEnrollButtonClick: boolean = false, plan?: Product<PlanAttributes, SummaryView>){
        const displayPage: string = messageCatalogContent.get("displayPage");

        if(displayPage === 'viewPlans'){
            this.plansStateSubscription = this.plansStateChanges$.subscribe(async plansState => {
                if(plansState?.['plansByPlanType'] && plansState?.['activeProductGroup']){
                    this.allPlanInfoLoaded(displayPage, isEnrollButtonClick, plan).then(responseForDisplay => {
                        this.displayResponseSubject.next(responseForDisplay);
                    })
                }
            })
        }else if(displayPage === 'planDetails' || displayPage === 'comparePlans'){
            this.plansStateSubscription = this.planDetailsStateChanges$.pipe(filter(planDetails => !!planDetails),take(1)).subscribe(async planDetails => {
                if(planDetails){
                    this.allPlanInfoLoaded(displayPage, isEnrollButtonClick).then(responseForDisplay => {
                       this.displayResponseSubject.next(responseForDisplay)
                    })
                }
            });
        }
    }

    ngOnDestroy(): void {
        if(this.plansStateSubscription){
            this.plansStateSubscription.unsubscribe();
        }
        if(this.planDetailsStateSubscription){
            this.planDetailsStateSubscription.unsubscribe();
        }
    }

    private async allPlanInfoLoaded(displayPage: string, isEnrollButtonClick: boolean = false, plan?: Product<PlanAttributes, SummaryView>): Promise<[Map<string, string>, boolean]> {
        let payload;
        if(displayPage === "viewPlans"){
            payload = this.getMessageServicePayload(displayPage, plan);
        }else{
            payload = this.getMessageServicePayload(displayPage);
        }
        try {
            const response = await this.retrieveZipCountyMessage(payload);
            if (response.status === "Success" && response.statusCode === 200) {
                return [this.parseResponseForDisplay(response.messages), isEnrollButtonClick];
            } else {
                return [new Map<string, string>(), isEnrollButtonClick];
            }
        } catch (error) {
            console.error("Error in allPlanInfoLoaded for displayPage:", displayPage, error);
            return [new Map<string, string>(), isEnrollButtonClick];
        }
    }

    private getMessageServicePayload(displayPage: string, plan?: Product<PlanAttributes, SummaryView> ): MessageCatalogPayloadStructure {
        const zipCountyInfo = this.reducerUtilityService.getComponentStates().ZipCounty
        const ddToken = this.brandService.getCurrentBrand().ddToken;
        const zipCode = zipCountyInfo.zipCode;
        const county = zipCountyInfo.county.county;
        const year = this.reducerUtilityService.getApplicationStates().EffectiveDate.effectiveDateYear;
        const planState = this.reducerUtilityService.getComponentStates()['Plans'] as PlansStateStructure;

        let payload: MessageCatalogPayloadStructure = 
        {
            "aspId": environment.initData.brand.ASPID,
            "region": zipCountyInfo.county.region,
            "market": "Individual",
            "productGroup": planState.activeProductGroup,
            "ddToken": ddToken,
            "year": year,
            "criteria": [],
        }

        //for compare plans only add the plans we are comparing to the criteria 
        if(displayPage === 'comparePlans'){
            const plans = this.planUtilityService.getPlanDetailsByProductGroupAsArray();
            for(const storedPlan of plans){
                const tempSubCriteria = [zipCode, county, storedPlan?.attributes?.HIOS_ID, storedPlan?.attributes?.METAL_LEVEL];
                payload.criteria.push(tempSubCriteria);
            }
        }else if(displayPage === 'planDetails'){
            const storedPlan = this.planUtilityService.getPlanDetailsByProductGroupAsArray();
            payload.criteria = [[zipCode, county, storedPlan[0]?.attributes?.HIOS_ID, storedPlan[0]?.attributes?.METAL_LEVEL]];

        }else if(displayPage === 'viewPlans'){
            //add plan that is being attempted to enroll in
            payload.criteria = [[zipCode, county, plan?.attributes?.HIOS_ID, plan?.attributes?.METAL_LEVEL]];
        }
        return payload;
    }

    private async retrieveZipCountyMessage(payload: MessageCatalogPayloadStructure): Promise<MessageCatalogResponse> {
        const requestConfig = new RequestConfigImplementation({
            method: REQUEST_METHODS.POST,
            url: `/retrieveIndividualPlans/messagingCatalog`,
            POST_OR_PUT_PAYLOAD: payload
        });

        this.httpClient.setStrategy(XHR_STRATEGIES.GOOGLE_CLOUD);
        return this.httpClient.makeRequest<MessageCatalogResponse>(requestConfig);
    }
    
    private getPlanNameForDisplay(hiosId: string){
        //get plan name by using the HIOS_ID to grab the plan from the array of ALL products available to a user
        const allPlans = this.planUtilityService.getPlansByProductGroupAsArray();
        const currentPlan = allPlans.filter((plan) => plan.attributes.HIOS_ID === hiosId);
        if(currentPlan.length > 0){
            return currentPlan[0].attributes.PRODUCT_NAME;
        }
        return "";
    }

    private parseResponseForDisplay(messages: MessagesResponseStructure[]): Map<string, string>{
    //make a message map to avoid duplicate messages being stored
        let messageMap = new Map();
        for(let i = 0; i < messages.length; i++){
            if(messages[i].messageArray.length !== 0){
            for(let j = 0; j < messages[i].messageArray.length; ++j){
                //make the response criteria a readible string by getting the plan name and/or formatting the multiple response criteria
                // ex: 55155 - Ramsey - Plan Name
                const currentMsg: MessageArrayStructure = [...messages][i].messageArray[j];
                const responseCriteriaArray: string[] = currentMsg.responseCriteria.map( crit => {
                    const planName = this.getPlanNameForDisplay(crit);
                    if(planName != ""){
                    return planName;
                    }
                    return crit;
                });
                const criteriaHeader = responseCriteriaArray.length > 1 ? responseCriteriaArray.join(' - ') : responseCriteriaArray?.[0];
                //replace the response criteria in the array with our newly formatted header
                currentMsg.responseCriteria = [criteriaHeader];

                //user the header as our key and message as value in the map, add it if we don't already have that key / value pair in our maps
                if(!messageMap.get(currentMsg.responseCriteria[0])){
                    messageMap.set(currentMsg.responseCriteria[0], currentMsg.message);
                }
            }
            }
        }
        return messageMap;
    }

    openMessageCatalogModal(messageCatalogContent: Map<string, any>, responseForDisplay: Map<string, string>, isEnrollButtonClick: boolean = false){
        let configs;

        if(isEnrollButtonClick){
            configs = {  
                modalHeading: messageCatalogContent.get("enrollModalHeading"),
                rightButtonLabel: messageCatalogContent.get("enrollRightButtonLabel"),
                newTabRightButtonUrl: messageCatalogContent.get("enrollNewTabRightButtonUrl"),
                rightButtonRedirectUrl: messageCatalogContent.get("enrollRightButtonRedirectUrl"),
                showLeftButton: messageCatalogContent.get("enrollShowLeftButton"),
                leftButtonLabel: messageCatalogContent.get("enrollLeftButtonLabel"),
                newTabLeftButtonUrl: messageCatalogContent.get("enrollNewTabLeftButtonUrl"),
                leftButtonRedirectUrl: messageCatalogContent.get("enrollLeftButtonRedirectUrl"),
                emphasizedText: messageCatalogContent.get("enrollEmphasizedText"),
                displayPage: messageCatalogContent.get("displayPage"),
                responseForDisplay: responseForDisplay
            }
        }else{
            configs = {
                modalHeading: messageCatalogContent.get("modalHeading"),
                rightButtonLabel: messageCatalogContent.get("rightButtonLabel"),
                newTabRightButtonUrl: messageCatalogContent.get("newTabRightButtonUrl"),
                rightButtonRedirectUrl: messageCatalogContent.get("rightButtonRedirectUrl"),
                showLeftButton: messageCatalogContent.get("showLeftButton"),
                leftButtonLabel: messageCatalogContent.get("leftButtonLabel"),
                newTabLeftButtonUrl: messageCatalogContent.get("newTabLeftButtonUrl"),
                leftButtonRedirectUrl: messageCatalogContent.get("leftButtonRedirectUrl"),
                emphasizedText: messageCatalogContent.get("emphasizedText"),
                displayPage: messageCatalogContent.get("displayPage"),
                responseForDisplay: responseForDisplay
            }
        }
        return this.matDialog.open(MessageCatalogModalComponent, {
            minWidth: "350px",
            maxWidth: "30vw",
            maxHeight: "90vh",
            disableClose: true,
            data: configs
        })

    }
    
}